import { Button, Dropdown, TableColumnsType, TablePaginationConfig, Tooltip, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { InfoTableHelper, WrapperPage } from '@components/atoms';
import { GenericList } from '@components/molecules';
import { useJobApplications } from '@hooks';
import { getJobApplicationsWithFilters } from '@hooks/useJobApplications/useJobApplications';
import { DownloadSimple, Trash } from '@phosphor-icons/react';

const { Paragraph } = Typography;

enum OrderEnum {
  ascend = 'asc',
  descend = 'desc',
}

type FilterType = 'All' | 'Operations' | 'Management' | 'Snow Removal' | 'Maintenance' | 'Administration';

type ApplicationStatus = 'pending' | 'reviewed' | 'contacted' | 'rejected' | 'hired';

export const TalentPoolPage = () => {
  const { updateJobApplicationStatus, deleteJobApplication, getResumeUrl } = useJobApplications();

  const [activeFilter, setActiveFilter] = useState<FilterType>('All');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | undefined>('updatedAt');
  const [sortOrder, setSortOrder] = useState<string | undefined>('descend');

  const params = {
    page,
    limit,
    search,
    department: activeFilter !== 'All' ? activeFilter : undefined,
    orderBy: sortField,
    orderType: sortOrder ? OrderEnum[sortOrder as keyof typeof OrderEnum] : undefined,
  };

  // Fetch applications using the hook with filters
  const { data, isLoading, refetch } = getJobApplicationsWithFilters(params);

  // Refetch quando os parâmetros mudam
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      refetch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [page, limit, search, sortOrder, sortField, activeFilter, refetch]);

  // Check if data exists before accessing properties
  const applications = Array.isArray(data) ? data : [];
  const total = applications.length || 0;

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: any,
    sorter: any
  ) => {
    const { current, pageSize } = pagination;

    if (pageSize !== limit) {
      setLimit(pageSize || 10);
      setPage(1);
      return;
    }

    if (current !== page) {
      setPage(current || 1);
      return;
    }

    // Verifique se sorter é um array e use o primeiro elemento se for
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    // Verifique se activeSorter tem propriedade field ou order antes de continuar
    if (!activeSorter || !activeSorter.field || !activeSorter.order) {
      return;
    }

    if (activeSorter.field !== sortField || activeSorter.order !== sortOrder) {
      setSortField(activeSorter.field as string);
      setSortOrder(activeSorter.order as string);
      setPage(1);
    }
  };

  const handleStatusChange = (id: string, status: ApplicationStatus) => {
    updateJobApplicationStatus.mutate({ id, data: { status } });
  };

  const columns: TableColumnsType<any> = [
    {
      title: 'CANDIDATE',
      key: 'candidate',
      dataIndex: 'fullName',
      render: (_: any, record: any) => (
        <InfoTableHelper
          title={record.fullName}
          subTitle={record.email}
        />
      ),
      sorter: true,
    },
    {
      title: 'POSITION',
      key: 'position',
      dataIndex: ['position', 'title'],
      render: (_: any, record: any) => (
        <span className="capitalize !font-orator">{record.position?.title}</span>
      ),
      sorter: true,
    },
    {
      title: 'SALARY',
      key: 'salary',
      dataIndex: ['position', 'salaryRange'],
      render: (_: any, record: any) => (
        <span className="capitalize !font-orator">{record.position?.salaryRange}</span>
      ),
      sorter: true,
    },
    {
      title: 'DEPARTMENT',
      key: 'department',
      dataIndex: ['position', 'department'],
      render: (_: any, record: any) => (
        <span className="capitalize !font-orator">{record.position?.department}</span>
      ),
      sorter: true,
    },
    {
      title: 'LOCATION',
      key: 'location',
      dataIndex: ['position', 'location'],
      render: (_: any, record: any) => (
        <span className="capitalize !font-orator">{record.position?.location}</span>
      ),
      sorter: true,
    },
    {
      title: 'STATUS',
      key: 'status',
      dataIndex: 'status',
      render: (_: any, record: any) => (
        <Dropdown
          menu={{
            items: [
              { key: 'pending', label: 'Pending' },
              { key: 'reviewed', label: 'Reviewed' },
              { key: 'contacted', label: 'Contacted' },
              { key: 'rejected', label: 'Rejected' },
              { key: 'hired', label: 'Hired' },
            ].map(status => ({
              key: status.key,
              label: (
                <span
                  className={`capitalize ${record.status === status.key ? 'text-orangicide' : ''}`}
                  onClick={() => handleStatusChange(record.id, status.key as ApplicationStatus)}
                >
                  {status.label}
                </span>
              ),
            })),
          }}
          trigger={['click']}
        >
          <Button type="text" className="!text-white hover:!text-orangicide">
            <span className="capitalize">{record.status}</span>
          </Button>
        </Dropdown>
      ),
      sorter: true,
    },
    {
      title: 'APPLIED AT',
      key: 'createdAt',
      dataIndex: 'createdAt',
      render: (_: any, record: any) =>
        record.createdAt ? dayjs(record.createdAt).format('MM/DD/YYYY') : '-',
      sorter: true,
    },
    {
      title: 'LAST UPDATED',
      key: 'updatedAt',
      dataIndex: 'updatedAt',
      render: (_: any, record: any) =>
        record.updatedAt ? dayjs(record.updatedAt).format('MM/DD/YYYY - HH:mm') : '-',
      sorter: true,
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 120,
      render: (_: any, record: any) => (
        <div className="flex gap-2">
          {record.resume && (
            <Tooltip title="Download Resume">
              <Button
                icon={<DownloadSimple size={16} />}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(getResumeUrl(record.resume), '_blank');
                }}
                type="text"
                className="!text-orangicide hover:!text-white"
              />
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <Button
              icon={<Trash size={16} />}
              onClick={(e) => {
                e.stopPropagation();
                deleteJobApplication.mutate(record.id);
              }}
              type="text"
              danger
              className="!text-red-500 hover:!text-white"
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative w-full h-[500px]">
        <div className="bg-[url(/assets/images/AboutBackground.png)] bg-center bg-cover absolute inset-0 w-full h-full"></div>
        <div className="container relative h-full flex flex-col justify-center items-center mx-auto px-4 py-12 md:py-16 lg:py-20 z-10">
          <div className="font-displayed !font-normal text-orangicide text-6xl text-center">
            TALENT POOL
          </div>
          <div className="max-w-3xl mx-auto">
            <Paragraph className="font-orator !text-white text-center md:!text-lg lg:!text-3xl pt-4">
              View and manage all job applications
            </Paragraph>
          </div>
        </div>
      </section>

      <section className="bg-[url(/assets/images/Background.png)] bg-center bg-cover bg-no-repeat w-full py-15">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-6">
            <div className="font-displayed !font-normal md:!text-lg lg:!text-5xl text-orangicide">TALENT POOL</div>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              //onClick={openAddPositionModal}
              className="btn-orangicide text-black font-orator !text-xl w-55 !h-12"
            >
              ADD POSITION
            </Button>
          </div>

          <Paragraph className="!text-white font-orator !text-xl tracking-wider pb-8">
            View, edit or remove applications from the system
          </Paragraph>

          {/* Filtros por tipo de vaga */}
          <div className="flex flex-wrap justify-start gap-2 mb-12">
            {(['All', 'Operations', 'Management', 'Snow Removal', 'Maintenance', 'Administration'] as FilterType[]).map((filter) => (
              <Button
                key={filter}
                type="default"
                className={`btn-orangicide ${activeFilter === filter ? 'filter-active' : ''}`} //adicionar classe para esses botões de filtro
                onClick={() => {
                  setActiveFilter(filter);
                }}
              >
                {filter}
              </Button>
            ))}
          </div>

          <WrapperPage isLoading={isLoading}>
            <GenericList<any>
              columns={columns}
              dataSource={applications}
              rowKey={(record) => record?.id || Math.random().toString()}
              onRow={(record) => ({
                onClick: () => record,
              })}
              onChange={handleTableChange}
              onSearch={handleSearch}
              pagination={{
                current: page,
                pageSize: limit,
                total: total,
                onChange(page, pageSize) {
                  setPage(page);
                  setLimit(pageSize || 10);
                },
                showSizeChanger: true,
              }}
            />
          </WrapperPage>
        </div>
      </section>
    </div>
  );
};
